// Pure helpers for admin user management.
// Extracted so they can be unit-tested without any DOM or network.

import type { AdminUser } from "./api";

/**
 * Filter a list of AdminUser records by a search query.
 *
 * The query is matched case-insensitively against both the user's name and
 * email address. An empty/blank query returns all users unchanged.
 */
export function filterUsers(users: AdminUser[], query: string): AdminUser[] {
  const q = query.trim().toLowerCase();
  if (!q) return users;
  return users.filter(
    (u) =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
  );
}

/**
 * Return a human-readable display name for a user.
 *
 * Prefers `name` when non-empty; falls back to the part of the email before
 * the `@` so the table is never blank.
 */
export function userDisplayName(user: AdminUser): string {
  const n = user.name.trim();
  if (n.length > 0) return n;
  return user.email.split("@")[0] ?? user.email;
}

/**
 * Format a last-login ISO timestamp as a short German locale date string.
 * Returns "—" when the user has never logged in.
 */
export function formatLastLogin(isoString: string | null): string {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

/**
 * Collect the badge labels to show for a user row (Bot, Admin, Extern,
 * Inaktiv). Returned in display order; caller renders each with its own
 * styling.
 *
 * "bot" steht vorn: dass hinter einer Zeile kein Mensch steckt, ist die
 * wichtigste Information über den Account.
 */
export type UserBadge = "bot" | "admin" | "extern" | "inaktiv";

export function userBadges(user: AdminUser): UserBadge[] {
  const badges: UserBadge[] = [];
  if (user.is_bot === true) badges.push("bot");
  if (user.is_admin) badges.push("admin");
  if (user.is_external) badges.push("extern");
  if (!user.is_active) badges.push("inaktiv");
  return badges;
}
